import { useInfiniteQuery, useMutation, useQuery } from 'react-query'
import { toApiFilterParams } from 'utils'

import { API_ENDPOINTS } from 'config/api'
import axios from 'config/axios'

import {
  AlgoChallenge,
  AlgoChallengeApiResponse,
  AllAlgoChallenges,
  AllAlgoChallengesApiResponse,
  AllFrontendChallenges,
  AllFrontendChallengesApiResponse,
  ChallengeCompany,
  ChallengeCompanyApiResponse,
  ChallengeLeaderboardApiResponse,
  ChallengesFilterParam,
  ChallengeTemplate,
  ChallengeTemplateApiResponse,
  CompanyApiResponse,
  FinalCodeResult,
  FinalCodeResultApiResponse,
  LanguagesApiResponse,
  NextAlgoQuestionDetails,
  NextAlgoQuestionResponse,
  PaginatedAllAlgoChallenges,
  PaginatedAllAlgoChallengesApiResponse,
  PaginatedAllFrontendChallenges,
  PaginatedAllFrontendChallengesApiResponse,
  ResultFetchDetailsStateType,
  RunAndSubmitCodePostData,
  RunAndSubmitCodeResponse,
  SubmissionHistory,
  SubmissionHistoryApiResponse
} from './AlgoEditor.types'

/**
 * for fetching all the questions
 */
export const fetchAllAlgoChallenges = async (
  options?: ChallengesFilterParam
) => {
  const params = toApiFilterParams({ ...options }, true)

  const response = await axios.get(
    `${API_ENDPOINTS.ALGO_EDITOR_QUESTIONS}?${params}`
  )
  const data = response.data as AllAlgoChallengesApiResponse

  return data.data.map((challenge) => ({
    ...challenge.attributes,
    id: challenge.id
  }))
}

export const useAllAlgoChallenges = ({
  options = {},
  enabled = true
}: {
  options?: ChallengesFilterParam
  enabled?: boolean
}) => {
  return useQuery<AllAlgoChallenges[], null>(
    ['All-Algo-Components', { options }],
    () => fetchAllAlgoChallenges(options),
    {
      enabled
    }
  )
}

/**
 * for fetching specific question
 */

export const fetchAlgoChallenge = async (slug: string) => {
  const response = await axios.get(
    `${API_ENDPOINTS.ALGO_EDITOR_QUESTIONS}/fetch_by_slug?slug=${slug}`
  )
  const data = response.data as AlgoChallengeApiResponse

  const { difficulty: difficultyLevel, ...restAttributes } =
    data.data.attributes
  const difficulty = difficultyLevel.replace('_level', '')

  return { ...restAttributes, difficulty, id: data.data.id }
}

// export const useAlgoChallenge = (slug: string) => {
//   return useQuery<AlgoChallenge, null>(
//     ['', { slug }],
//     () => fetchAlgoChallenge(slug),
//     {
//       enabled: !!slug
//     }
//   )
// }

/*
 Question with Templates
 */

export const fecthAlgoQuestionDetailswithTemplate = async (slug: string) => {
  const question = await fetchAlgoChallenge(slug)
  const templates = await fetchChallengeTemplate(question.id)

  return { question, templates }
}

export const useAlgoQuestionDetailswithTemplate = (slug: string) => {
  return useQuery<
    { question: AlgoChallenge; templates: ChallengeTemplate[] },
    null
  >(
    ['Algo-Challenge', { slug }],
    () => fecthAlgoQuestionDetailswithTemplate(slug),
    {
      enabled: !!slug
    }
  )
}

/**
 * Test Cases Submission
 */
export const fetchTestCasesSubmissionId = async (
  codeData: RunAndSubmitCodePostData
) => {
  const response = await axios.post(
    `${API_ENDPOINTS.SUBMIT_CODE}?run_code=true`,
    {
      data: { attributes: codeData, type: 'algo_submissions' }
    }
  )

  const data = response.data as RunAndSubmitCodeResponse

  return data.data.attributes.id
}

export const useTestCasesSubmissionMutation = () => {
  return useMutation(fetchTestCasesSubmissionId)
}

/**
 * Final Code Submission
 */
export const fetchCodeSubmissionId = async (
  codeData: RunAndSubmitCodePostData
) => {
  const response = await axios.post(`${API_ENDPOINTS.SUBMIT_CODE}`, {
    data: { attributes: codeData, type: 'algo_submission' }
  })

  const data = response.data as RunAndSubmitCodeResponse

  return data.data.attributes.id
}

export const useCodeSubmissionMutation = () => {
  return useMutation(fetchCodeSubmissionId)
}

/**
 * Get the result of the code submission
 */
export const fetchCodeSubmissionResult = async (
  submissionId: number,
  mode: string
) => {
  const URL =
    mode === 'submit-run'
      ? API_ENDPOINTS.SUBMIT_CODE
      : API_ENDPOINTS.TEST_RUN_CODE
  const response = await axios.get(`${URL}/${submissionId}`)
  const data = response.data as FinalCodeResultApiResponse

  return {
    ...data.data.attributes,
    result_id: data.data.id
  }
}

export const useAlgoSubmissionResult = ({
  submissionId,
  mode
}: ResultFetchDetailsStateType) => {
  return useQuery<FinalCodeResult, null>(
    ['Algo-Submission-Result', { submissionId }],
    () => fetchCodeSubmissionResult(submissionId as number, mode),
    {
      enabled: !!submissionId,
      refetchOnWindowFocus: 'always'
    }
  )
}

/**
 * Get the submission history
 */
export const fetchCodeSubmissionHistory = async (challengeId: number) => {
  const response = await axios.get(
    `${API_ENDPOINTS.ALGO_EDITOR_QUESTIONS}/${challengeId}/submissions`
  )
  const data = response.data as SubmissionHistoryApiResponse

  data.data.attributes.submissions.sort((a, b) => {
    return b.id - a.id
  })

  return {
    ...data.data.attributes
  }
}

export const useSubmissionHistory = (challengeId: number) => {
  return useQuery<SubmissionHistory, null>(
    ['Submission-History', { challengeId }],
    () => fetchCodeSubmissionHistory(challengeId),
    {
      enabled: !!challengeId
    }
  )
}

/**
 * Get All Companies Listed
 */

export const fetchAllCompanies = async () => {
  const response = await axios.get(API_ENDPOINTS.COMPANY)
  const data = response.data as CompanyApiResponse

  return data.data.map(({ id, attributes }) => ({ id, ...attributes }))
}

export const useCompanies = () => {
  return useQuery<Array<{ id: string; name: string; image_url: string }>>(
    ['companies'],
    fetchAllCompanies
  )
}

/**
 * Fetch all company for a challenge
 */

export const fetchCompanyForSpecificChallenge = async (challengeId: number) => {
  const response = await axios.get(
    `${API_ENDPOINTS.ALGO_EDITOR_QUESTIONS}/${challengeId}/companies`
  )
  const data = response.data as ChallengeCompanyApiResponse

  return data.data.attributes
}

export const useChallengeCompany = (challengeId: number) => {
  return useQuery<ChallengeCompany, null>(
    ['company-challenge', { challengeId }],
    () => fetchCompanyForSpecificChallenge(challengeId),
    { enabled: !!challengeId }
  )
}

export const fetchLanguages = async () => {
  const response = await axios.get(API_ENDPOINTS.ALGO_LANGS)
  const data = response.data as LanguagesApiResponse
  return data.data.map((language) => ({
    ...language.attributes,
    id: language.id
  }))
}

export const useLanguages = () => {
  return useQuery(['languages'], fetchLanguages)
}

export const fetchChallengeTemplate = async (challengeId: number) => {
  const response = await axios.get(
    `${API_ENDPOINTS.ALGO_EDITOR_QUESTIONS}/${challengeId}/template`
  )

  const data = response.data as ChallengeTemplateApiResponse
  return Object.entries(data.data.attributes.templates).map(
    ([_key, val]) => val
  )
}

export const useChallengeTemplate = (challengeId: number) => {
  return useQuery<ChallengeTemplate[]>(
    ['Challenge-Templates', challengeId],
    () => fetchChallengeTemplate(challengeId),
    {
      enabled: !!challengeId
    }
  )
}

/**
 * fetch all challenge scoreboard
 * */
export const useChallengeLeaderBoard = (challengeId: number) => {
  return useInfiniteQuery<ChallengeLeaderboardApiResponse, null>(
    ['Challenge-Leaderboard'],
    ({ pageParam = 1 }) => fetchChallengeLeaderboard(challengeId, pageParam),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.id < lastPage.count) {
          return lastPage.id + 1
        }
        return undefined
      }
    }
  )
}

export const fetchChallengeLeaderboard = async (
  challengeId: number,
  pageParam: number
) => {
  const response = await axios.get(
    `${API_ENDPOINTS.CHALLENGE_LEADERBOARD}?id=${challengeId}&page=${pageParam}`
  )
  return response.data.data.attributes
}

/**
 * Fetch Next Algo Question
 */

export const fetchNextAlgoQuestion = async (current_question_id: number) => {
  const response = await axios.get(
    `${API_ENDPOINTS.NEXT_ALGO_QUESTION}?current_question_id=${current_question_id}`
  )

  const { data } = response.data as NextAlgoQuestionResponse
  return data.attributes
}

export const useFetchNextAlgoQuestion = (
  current_question_id: number,
  isFetching: boolean
) => {
  return useQuery<NextAlgoQuestionDetails, null>(
    ['next-algo-question', current_question_id],
    () => fetchNextAlgoQuestion(current_question_id),
    {
      enabled: isFetching
    }
  )
}

/**
 * for fetching all the Frontend questions
 */
export const fetchAllFrontendChallenges = async (
  options?: ChallengesFilterParam
) => {
  const params = toApiFilterParams({ ...options }, true)

  const response = await axios.get(
    `${API_ENDPOINTS.FRONTEND_CHALLEGENS}?is_active=true&${params}`
  )
  const data = response.data as AllFrontendChallengesApiResponse

  return data.data.map((challenge) => ({
    ...challenge.attributes,
    id: challenge.id
  }))
}

export const useAllFrontendChallenges = ({
  options = {},
  enabled = true
}: {
  options?: ChallengesFilterParam
  enabled?: boolean
}) => {
  return useQuery<AllFrontendChallenges[], null>(
    ['All-Frontend-Challenges', { options }],
    () => fetchAllFrontendChallenges(options),
    {
      enabled
    }
  )
}

/**
 * Fetching Paginated Challenges
 */

type PaginatedProps = {
  currentPage: number
  pageSize: number
  options?: ChallengesFilterParam
}

// Paginated Algo Challenges
export const fetchPaginatedAllAlgoChallenges = async (
  props: PaginatedProps
) => {
  const { currentPage, pageSize, options } = props

  const params = toApiFilterParams({ ...options }, true)

  const response = await axios.get(
    `${API_ENDPOINTS.ALGO_EDITOR_QUESTIONS}?${params}&page[number]=${currentPage}&page[size]=${pageSize}`
  )
  const { data, links } =
    (await response.data) as PaginatedAllAlgoChallengesApiResponse

  return {
    challenges: data.map((challenge) => ({
      ...challenge.attributes,
      id: challenge.id
    })),
    links
  }
}

export const usePaginatedAllAlgoChallenges = ({
  pageSize,
  options = {},
  enabled = true
}: {
  pageSize: number
  options?: Record<string, any>
  enabled?: boolean
}) => {
  return useInfiniteQuery<PaginatedAllAlgoChallenges, Error>(
    ['Paginated-All-Algo-Challenges', { options }],
    ({ pageParam: currentPage = 1 }) =>
      fetchPaginatedAllAlgoChallenges({ currentPage, pageSize, options }),
    {
      enabled,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.links.next
        return nextPage
          ? Number(new URL(nextPage).searchParams.get('page[number]'))
          : undefined
      }
    }
  )
}

// Paginated Frontend Challenges
export const fetchPaginatedAllFrontendChallenges = async (
  props: PaginatedProps
) => {
  const { currentPage, pageSize, options } = props

  const params = toApiFilterParams({ ...options }, true)

  const response = await axios.get(
    `${API_ENDPOINTS.FRONTEND_CHALLEGENS}?${params}&page[number]=${currentPage}&page[size]=${pageSize}`
  )
  const { data, links } =
    (await response.data) as PaginatedAllFrontendChallengesApiResponse

  return {
    challenges: data.map((challenge) => ({
      ...challenge.attributes,
      id: challenge.id
    })),
    links
  }
}

export const usePaginatedAllFrontendChallenges = ({
  pageSize,
  options = {},
  enabled = true
}: {
  pageSize: number
  options?: Record<string, any>
  enabled?: boolean
}) => {
  return useInfiniteQuery<PaginatedAllFrontendChallenges, Error>(
    ['Paginated-All-Frontend-Challenges', { options }],
    ({ pageParam: currentPage = 1 }) =>
      fetchPaginatedAllFrontendChallenges({ currentPage, pageSize, options }),
    {
      enabled,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.links.next
        return nextPage
          ? Number(new URL(nextPage).searchParams.get('page[number]'))
          : undefined
      }
    }
  )
}
